import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Award, Link2, RotateCcw } from 'lucide-react';
import {
  clearActivityScore,
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export interface MatchingCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  leftItems: string[];
  rightItems: string[];
  pairs: { left: string; right: string }[];
  sourcePage?: number;
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type PersistShape = { links: (number | null)[] };

type RowMark = 'neutral' | 'correct' | 'wrong';

type PendingPick = { side: 'left' | 'right'; index: number };

type LineSeg = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  mark: 'correct' | 'wrong';
  /** Index into {@link CORRECT_PAIR_THEMES}; only for correct segments. */
  themeIndex: number | null;
};

/** Visual theme for one correctly matched pair (line + both cards). Cycles for 5+ pairs. */
export type MatchingPairTheme = {
  id: string;
  lineClass: string;
  cardClass: string;
  labelClass: string;
  bodyClass: string;
};

export const CORRECT_PAIR_THEMES: readonly MatchingPairTheme[] = [
  {
    id: 'orange',
    lineClass: 'stroke-orange-500 dark:stroke-orange-400',
    cardClass:
      'border-orange-500 bg-orange-50 dark:border-orange-400 dark:bg-orange-950/50',
    labelClass: 'text-orange-800 dark:text-orange-200',
    bodyClass: 'text-orange-950 dark:text-orange-50',
  },
  {
    id: 'blue',
    lineClass: 'stroke-blue-600 dark:stroke-blue-400',
    cardClass: 'border-blue-600 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/50',
    labelClass: 'text-blue-900 dark:text-blue-200',
    bodyClass: 'text-blue-950 dark:text-blue-50',
  },
  {
    id: 'purple',
    lineClass: 'stroke-violet-600 dark:stroke-violet-400',
    cardClass: 'border-violet-600 bg-app-primary/10 dark:border-violet-400 dark:bg-app-primary/15/50',
    labelClass: 'text-app-primary dark:text-violet-200',
    bodyClass: 'text-app-primary dark:text-violet-50',
  },
  {
    id: 'green',
    lineClass: 'stroke-green-600 dark:stroke-green-400',
    cardClass: 'border-green-600 bg-green-50 dark:border-green-500 dark:bg-green-950/50',
    labelClass: 'text-green-900 dark:text-green-200',
    bodyClass: 'text-green-950 dark:text-green-50',
  },
] as const;

/** Build theme map: k-th correct pair (by completion order) uses palette index k % 4. */
function themeIndexMapFromOrder(correctPairOrder: readonly number[]): Map<number, number> {
  const map = new Map<number, number>();
  correctPairOrder.forEach((leftIdx, order) => {
    map.set(leftIdx, order % CORRECT_PAIR_THEMES.length);
  });
  return map;
}

function isPermutationOf(order: string[], rights: string[]): boolean {
  if (order.length !== rights.length) return false;
  const a = [...order].sort();
  const b = [...rights].sort();
  return a.every((v, i) => v === b[i]);
}

function normalizeLinks(raw: unknown, n: number): (number | null)[] | null {
  if (!Array.isArray(raw) || raw.length !== n) return null;
  const next: (number | null)[] = [];
  for (let i = 0; i < n; i++) {
    const v = raw[i];
    if (v === null || v === undefined) {
      next.push(null);
      continue;
    }
    if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < n) next.push(v);
    else return null;
  }
  return next;
}

function migrateFromOrder(order: string[], leftItems: string[], rightItems: string[]): (number | null)[] {
  const n = leftItems.length;
  return leftItems.map((_, i) => {
    const w = order[i];
    const j = rightItems.indexOf(w);
    return j >= 0 ? j : null;
  });
}

export const MatchingCard: React.FC<MatchingCardProps> = ({
  workbookLabel,
  title,
  instructions,
  leftItems,
  rightItems,
  pairs,
  sourcePage,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-match-${persistenceId}` : '';
  const lineGlowFilterId = useId().replace(/:/g, '');

  const expected = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pairs) m.set(p.left, p.right);
    return m;
  }, [pairs]);

  const n = leftItems.length;
  const canInteract =
    n > 0 &&
    rightItems.length === n &&
    pairs.length > 0 &&
    leftItems.every((l) => expected.has(l));

  const defaultLinks = useMemo(() => Array<number | null>(n).fill(null), [n]);

  const [links, setLinks] = useState<(number | null)[]>(defaultLinks);
  const baselineLinksRef = useRef<(number | null)[]>(defaultLinks);
  const [pending, setPending] = useState<PendingPick | null>(null);
  /** Left-row indices in order each row first became correct (drives pair colors). */
  const [correctPairOrder, setCorrectPairOrder] = useState<number[]>([]);
  const prevRowMarksRef = useRef<RowMark[] | null>(null);
  const [layoutTick, setLayoutTick] = useState(0);

  const boardRef = useRef<HTMLDivElement>(null);
  const leftRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const rightRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const rowMarks = useMemo((): RowMark[] => {
    return leftItems.map((left, i) => {
      const j = links[i];
      if (j === null || j === undefined) return 'neutral';
      const r = rightItems[j];
      return r === expected.get(left) ? 'correct' : 'wrong';
    });
  }, [leftItems, links, rightItems, expected]);

  const matchingScore = useMemo(() => {
    const correct = rowMarks.filter((m) => m === 'correct').length;
    const wrong = rowMarks.filter((m) => m === 'wrong').length;
    const neutral = rowMarks.filter((m) => m === 'neutral').length;
    const pct = n > 0 ? Math.round((correct / n) * 100) : 0;
    return { correct, wrong, neutral, pct };
  }, [rowMarks, n]);

  useEffect(() => {
    const prev = prevRowMarksRef.current;
    prevRowMarksRef.current = rowMarks;

    if (!prev || prev.length !== n) {
      const initial = rowMarks
        .map((m, i) => (m === 'correct' ? i : -1))
        .filter((i): i is number => i >= 0);
      setCorrectPairOrder(initial);
      return;
    }

    setCorrectPairOrder((ord) => {
      const stillCorrect = (i: number) => rowMarks[i] === 'correct';
      const kept = ord.filter(stillCorrect);
      const newly: number[] = [];
      for (let i = 0; i < n; i++) {
        if (rowMarks[i] === 'correct' && prev[i] !== 'correct') newly.push(i);
      }
      const seen = new Set(kept);
      const append = newly.filter((i) => !seen.has(i));
      return [...kept, ...append];
    });
  }, [rowMarks, n]);

  const themeIndexByLeftRow = useMemo(
    () => themeIndexMapFromOrder(correctPairOrder),
    [correctPairOrder]
  );

  useEffect(() => {
    leftRefs.current = leftRefs.current.slice(0, n);
    rightRefs.current = rightRefs.current.slice(0, n);
  }, [n]);

  useEffect(() => {
    if (!storageKey || !canInteract) {
      setLinks(defaultLinks);
      baselineLinksRef.current = defaultLinks;
      setPending(null);
      setCorrectPairOrder([]);
      prevRowMarksRef.current = null;
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> & { order?: string[] } | null>(storageKey, null);
    const fromLinks = loaded?.links != null ? normalizeLinks(loaded.links, n) : null;
    if (fromLinks) {
      setLinks(fromLinks);
      baselineLinksRef.current = [...fromLinks];
      setPending(null);
      prevRowMarksRef.current = null;
      return;
    }
    const o = loaded?.order;
    if (Array.isArray(o) && o.length === n && isPermutationOf(o as string[], rightItems)) {
      const migrated = migrateFromOrder(o as string[], leftItems, rightItems);
      setLinks(migrated);
      baselineLinksRef.current = [...migrated];
    } else {
      setLinks(defaultLinks);
      baselineLinksRef.current = defaultLinks;
    }
    setPending(null);
    prevRowMarksRef.current = null;
  }, [storageKey, canInteract, defaultLinks, n, rightItems.join('\u0001')]);

  useEffect(() => {
    if (!storageKey || !canInteract) return;
    writeWorkbookJson(storageKey, { links } satisfies PersistShape);
  }, [storageKey, canInteract, links]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const linksTouched = useMemo(
    () => JSON.stringify(links) !== JSON.stringify(baselineLinksRef.current),
    [links]
  );

  useEffect(() => {
    if (!onWorkbookStatusChange || !canInteract) {
      onWorkbookStatusChange?.('not_started');
      return;
    }
    const allLinked = links.every((j) => j !== null && j !== undefined);
    const everyGreen = allLinked && rowMarks.every((m) => m === 'correct');
    if (everyGreen) {
      onWorkbookStatusChange('completed');
      return;
    }
    onWorkbookStatusChange(linksTouched ? 'in_progress' : 'not_started');
  }, [onWorkbookStatusChange, canInteract, rowMarks, links, linksTouched]);

  const allRowsLinked = n > 0 && links.every((j) => j !== null && j !== undefined);
  useEffect(() => {
    if (!persistenceId || !canInteract || !allRowsLinked) return;
    writeActivityScore(persistenceId, (matchingScore.correct / n) * 100);
  }, [persistenceId, canInteract, allRowsLinked, matchingScore.correct, n]);

  useLayoutEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const bump = () => setLayoutTick((t) => t + 1);
    const ro = new ResizeObserver(bump);
    ro.observe(el);
    window.addEventListener('scroll', bump, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', bump, true);
    };
  }, []);

  useLayoutEffect(() => {
    setLayoutTick((t) => t + 1);
  }, [links, pending]);

  const lineSegments = useMemo((): LineSeg[] => {
    const board = boardRef.current;
    if (!board) return [];
    const c = board.getBoundingClientRect();
    const segs: LineSeg[] = [];
    for (let i = 0; i < n; i++) {
      const j = links[i];
      if (j === null || j === undefined) continue;
      const L = leftRefs.current[i];
      const R = rightRefs.current[j];
      if (!L || !R) continue;
      const lr = L.getBoundingClientRect();
      const rr = R.getBoundingClientRect();
      const left = lr.right - c.left;
      const topL = lr.top + lr.height / 2 - c.top;
      const right = rr.left - c.left;
      const topR = rr.top + rr.height / 2 - c.top;
      const mark = rowMarks[i] === 'correct' ? 'correct' : 'wrong';
      const themeIndex = mark === 'correct' ? (themeIndexByLeftRow.get(i) ?? 0) : null;
      segs.push({ x1: left, y1: topL, x2: right, y2: topR, mark, themeIndex });
    }
    return segs;
  }, [links, n, rowMarks, themeIndexByLeftRow, layoutTick]);

  const connect = useCallback(
    (leftIdx: number, rightIdx: number) => {
      setLinks((prev) => {
        const rowMark = (i: number): RowMark => {
          const j = prev[i];
          if (j === null || j === undefined) return 'neutral';
          const r = rightItems[j];
          return r === expected.get(leftItems[i]) ? 'correct' : 'wrong';
        };
        if (rowMark(leftIdx) === 'correct' || rowMark(leftIdx) === 'wrong') return prev;
        for (let k = 0; k < n; k++) {
          if (k !== leftIdx && prev[k] === rightIdx) {
            if (rowMark(k) === 'correct' || rowMark(k) === 'wrong') return prev;
          }
        }
        const next = [...prev];
        for (let k = 0; k < n; k++) {
          if (k !== leftIdx && next[k] === rightIdx) next[k] = null;
        }
        next[leftIdx] = rightIdx;
        return next;
      });
      setPending(null);
      mark();
    },
    [n, mark, leftItems, rightItems, expected]
  );

  const onLeftClick = (i: number) => {
    if (rowMarks[i] === 'correct' || rowMarks[i] === 'wrong') return;
    mark();
    if (pending?.side === 'left' && pending.index === i) {
      setPending(null);
      return;
    }
    if (pending?.side === 'right') {
      connect(i, pending.index);
      return;
    }
    setPending({ side: 'left', index: i });
  };

  const onRightClick = (j: number) => {
    const owner = links.findIndex((v) => v === j);
    if (owner >= 0 && (rowMarks[owner] === 'correct' || rowMarks[owner] === 'wrong')) return;

    mark();
    if (pending?.side === 'right' && pending.index === j) {
      setPending(null);
      return;
    }
    if (pending?.side === 'left') {
      connect(pending.index, j);
      return;
    }
    setPending({ side: 'right', index: j });
  };

  const resetAll = () => {
    mark();
    const fresh = Array<number | null>(n).fill(null);
    setLinks(fresh);
    baselineLinksRef.current = fresh;
    setPending(null);
    setCorrectPairOrder([]);
    prevRowMarksRef.current = null;
    if (storageKey) writeWorkbookJson(storageKey, { links: fresh } satisfies PersistShape);
    if (persistenceId) clearActivityScore(persistenceId);
    onWorkbookStatusChange?.('not_started');
  };

  const shell = (body: React.ReactNode, footer: React.ReactNode | null, hint?: React.ReactNode) => (
    <div className="overflow-hidden rounded-[2rem] border-4 border-teal-200/80 bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 shadow-[0_20px_50px_-12px_rgba(13,148,136,0.35)] dark:border-teal-800/60 dark:from-teal-950/50 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-teal-200/60 bg-white/50 px-5 py-4 backdrop-blur-sm dark:border-teal-800/40 dark:bg-slate-900/40">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            {workbookLabel ? (
              <p className="text-sm font-bold text-teal-700 tamil-text dark:text-teal-300" lang="ta">
                {workbookLabel}
              </p>
            ) : null}
            <h2 className="mt-1 text-2xl font-extrabold leading-snug text-teal-900 tamil-text md:text-3xl dark:text-teal-100" lang="ta">
              {title}
            </h2>
            {instructions ? (
              <p className="mt-3 text-lg leading-relaxed text-teal-900/90 tamil-text dark:text-teal-50/90" lang="ta">
                {instructions}
              </p>
            ) : null}
          </div>
          {sourcePage != null ? (
            <span className="rounded-full bg-teal-600 px-3 py-1 text-sm font-bold text-white shadow-md">p.{sourcePage}</span>
          ) : null}
        </div>
        {hint}
      </div>
      <div className="px-4 py-6 sm:px-6">{body}</div>
      {footer != null ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-teal-200/50 bg-white/40 px-4 py-4 backdrop-blur-sm dark:border-teal-800/40 dark:bg-slate-900/50 sm:px-6">
          {footer}
        </div>
      ) : null}
    </div>
  );

  if (!canInteract) {
    return shell(
      <p className="text-center text-lg leading-relaxed text-teal-900 tamil-text dark:text-teal-100" lang="ta">
        இந்தச் செயலுக்கு இடது/வலது வரிகள் சம எண்ணிக்கையிலும், ஒவ்வொரு இடதுக்கும் பொருத்தம் தேவை. தரவைச் சரிபார்க்கவும்.
      </p>,
      null
    );
  }

  const hint = (
    <div className="mt-4 rounded-2xl bg-teal-100/90 p-4 text-base font-semibold leading-relaxed text-teal-950 dark:bg-teal-900/40 dark:text-teal-50 tamil-text" lang="ta">
      முதலில் <strong>இடது</strong> அல்லது <strong>வலது</strong> ஒன்றைத் தட்டவும்; பின் பொருத்தத்தை <strong>மறுபக்கத்தில்</strong> தட்டவும் — கோடு இணைக்கும். ஒவ்வொரு சரி இணைப்பும் ஒரே வண்ணத்தில் (அட்டை + கோடு). தவறு சிவப்பு. ஒரு வரிக்கு ஒரு இணைப்பு மட்டும் — சரியானதும் தவறானதும் பின் மாற்ற இயலாது; மீண்டும் முயல் மூலம் முழுச் செயலை மீட்டமைக்கவும்.
    </div>
  );

  const footer = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <button
        type="button"
        onClick={resetAll}
        className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-2 border-teal-400 bg-white px-5 py-3 text-base font-bold text-teal-800 shadow-sm transition hover:bg-teal-50 dark:border-teal-600 dark:bg-slate-800 dark:text-teal-100 dark:hover:bg-slate-700 tamil-text"
        lang="ta"
      >
        <RotateCcw className="h-5 w-5 shrink-0" aria-hidden />
        மீண்டும் முயல் (கலைத்தல்)
      </button>
      {rowMarks.every((m) => m === 'correct') && links.every((j) => j !== null) ? (
        <p className="text-center text-lg font-black text-emerald-700 tamil-text dark:text-emerald-300 sm:text-right" lang="ta">
          அனைத்து வரிகளும் சரி!{' '}
          <span className="block text-base font-black tabular-nums text-emerald-800/90 dark:text-emerald-200/90">
            முழு மதிப்பெண் {matchingScore.correct}/{n} ({matchingScore.pct}%)
          </span>
        </p>
      ) : (
        <p className="text-center text-base font-bold text-amber-800 tamil-text dark:text-amber-200 sm:text-right" lang="ta">
          தவறான இணைப்பை மாற்ற இயலாது. அனைத்து வரிகளையும் இணைத்த பின் மதிப்பெண் பதிவாகும்.
          <span className="mt-1 block text-sm font-black tabular-nums text-teal-900 dark:text-teal-100">
            மதிப்பெண் {matchingScore.correct}/{n} ({matchingScore.pct}%)
            {matchingScore.wrong > 0 ? (
              <span className="ml-2 text-rose-700 dark:text-rose-300">· தவறு {matchingScore.wrong}</span>
            ) : null}
            {matchingScore.neutral > 0 ? (
              <span className="ml-2 opacity-80">· இணைப்பில்லை {matchingScore.neutral}</span>
            ) : null}
          </span>
        </p>
      )}
    </div>
  );

  const setLeftRef = (i: number) => (el: HTMLButtonElement | null) => {
    leftRefs.current[i] = el;
  };
  const setRightRef = (j: number) => (el: HTMLButtonElement | null) => {
    rightRefs.current[j] = el;
  };

  return shell(
    <div>
      <div className="mb-4 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/90 px-5 py-2 text-sm font-black text-teal-900 shadow-md dark:bg-slate-800 dark:text-teal-100 tamil-text" lang="ta">
            <Link2 className="h-4 w-4 shrink-0" aria-hidden />
            தட்டி கோடிணைப்போம்
          </div>
        </div>
        <div
          className="inline-flex min-h-[48px] items-center justify-center gap-2 self-center rounded-2xl border-2 border-amber-400/90 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-2 shadow-md dark:border-amber-600/80 dark:from-amber-950/50 dark:to-orange-950/40 tamil-text"
          lang="ta"
          role="status"
          aria-live="polite"
          aria-label={`மதிப்பெண் ${matchingScore.correct} முதல் ${n}, சதவீதம் ${matchingScore.pct}`}
        >
          <Award className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5">
            <span className="text-sm font-bold text-amber-900/90 dark:text-amber-100/90">மதிப்பெண்</span>
            <span className="text-xl font-black tabular-nums text-amber-950 dark:text-amber-50">
              {matchingScore.correct} / {n}
            </span>
            <span className="text-base font-bold tabular-nums text-amber-800 dark:text-amber-200">
              ({matchingScore.pct}%)
            </span>
            {matchingScore.wrong > 0 ? (
              <span className="text-sm font-bold text-rose-700 dark:text-rose-300">தவறு {matchingScore.wrong}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mb-2 hidden rounded-2xl bg-teal-800/10 px-4 py-2 text-sm font-black text-teal-900 dark:bg-teal-950/50 dark:text-teal-100 sm:grid sm:grid-cols-2 sm:gap-4 tamil-text" lang="ta">
        <span>இடது (தட்டவும்)</span>
        <span className="text-center">வலது (தட்டவும்)</span>
      </div>

      <div ref={boardRef} className="relative flex min-h-[120px] flex-row justify-between gap-6 sm:gap-10">
        <svg
          className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible"
          aria-hidden
        >
          <defs>
            <filter id={lineGlowFilterId} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {lineSegments.map((seg, idx) => {
            const theme =
              seg.mark === 'correct' && seg.themeIndex != null
                ? CORRECT_PAIR_THEMES[seg.themeIndex % CORRECT_PAIR_THEMES.length]
                : null;
            return (
              <line
                key={idx}
                x1={seg.x1}
                y1={seg.y1}
                x2={seg.x2}
                y2={seg.y2}
                strokeWidth={4}
                strokeLinecap="round"
                filter={`url(#${lineGlowFilterId})`}
                className={
                  seg.mark === 'correct' && theme
                    ? theme.lineClass
                    : 'stroke-rose-500 dark:stroke-rose-400'
                }
              />
            );
          })}
        </svg>

        <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-3">
          {leftItems.map((left, i) => {
            const rm = rowMarks[i];
            const locked = rm === 'correct' || rm === 'wrong';
            const sel = pending?.side === 'left' && pending.index === i;
            const pairTheme =
              rm === 'correct'
                ? CORRECT_PAIR_THEMES[
                    (themeIndexByLeftRow.get(i) ?? 0) % CORRECT_PAIR_THEMES.length
                  ]
                : null;
            return (
              <button
                key={`L-${i}`}
                type="button"
                ref={setLeftRef(i)}
                disabled={locked}
                onClick={() => onLeftClick(i)}
                className={clsx(
                  'min-h-[64px] w-full rounded-xl border-2 px-4 py-3 text-left shadow-md transition sm:min-h-[72px]',
                  locked && 'cursor-not-allowed opacity-95',
                  sel && 'ring-4 ring-amber-400 ring-offset-2 ring-offset-teal-50 dark:ring-offset-slate-900',
                  rm === 'correct' && pairTheme
                    ? pairTheme.cardClass
                    : rm === 'wrong'
                      ? 'border-rose-500 bg-rose-50 dark:border-rose-500 dark:bg-rose-950/40'
                      : 'border-teal-200/90 bg-white/90 hover:border-teal-400 dark:border-teal-700 dark:bg-slate-800/90 dark:hover:border-teal-500'
                )}
              >
                <p
                  className={clsx(
                    'text-xs font-bold uppercase tracking-wide',
                    rm === 'correct' && pairTheme
                      ? pairTheme.labelClass
                      : 'text-teal-700 dark:text-teal-300'
                  )}
                  lang="ta"
                >
                  இடது {i + 1}
                </p>
                <p
                  className={clsx(
                    'mt-1 text-lg font-black leading-snug tamil-text sm:text-xl',
                    rm === 'correct' && pairTheme
                      ? pairTheme.bodyClass
                      : 'text-teal-950 dark:text-teal-50'
                  )}
                  lang="ta"
                >
                  {left}
                </p>
              </button>
            );
          })}
        </div>

        <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-3">
          {rightItems.map((right, j) => {
            const usedBy = links.findIndex((v) => v === j);
            const rm = usedBy >= 0 ? rowMarks[usedBy] : 'neutral';
            const locked = rm === 'correct' || rm === 'wrong';
            const sel = pending?.side === 'right' && pending.index === j;
            const pairTheme =
              rm === 'correct' && usedBy >= 0
                ? CORRECT_PAIR_THEMES[
                    (themeIndexByLeftRow.get(usedBy) ?? 0) % CORRECT_PAIR_THEMES.length
                  ]
                : null;
            return (
              <button
                key={`R-${j}`}
                type="button"
                ref={setRightRef(j)}
                disabled={locked}
                onClick={() => onRightClick(j)}
                className={clsx(
                  'min-h-[64px] w-full rounded-xl border-2 px-4 py-3 text-center shadow-md transition sm:min-h-[72px]',
                  locked && 'cursor-not-allowed opacity-95',
                  sel && 'ring-4 ring-amber-400 ring-offset-2 ring-offset-teal-50 dark:ring-offset-slate-900',
                  rm === 'correct' && pairTheme
                    ? pairTheme.cardClass
                    : rm === 'wrong'
                      ? 'border-rose-500 bg-rose-50 dark:border-rose-500 dark:bg-rose-950/40'
                      : 'border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-pink-50 hover:border-fuchsia-400 dark:border-fuchsia-700 dark:from-fuchsia-950/40 dark:to-slate-900 dark:hover:border-fuchsia-500'
                )}
              >
                <p
                  className={clsx(
                    'text-xs font-bold uppercase tracking-wide',
                    rm === 'correct' && pairTheme
                      ? pairTheme.labelClass
                      : 'text-fuchsia-800 dark:text-fuchsia-300'
                  )}
                  lang="ta"
                >
                  வலது {j + 1}
                </p>
                <p
                  className={clsx(
                    'mt-1 text-lg font-black leading-snug tamil-text sm:text-xl',
                    rm === 'correct' && pairTheme
                      ? pairTheme.bodyClass
                      : 'text-fuchsia-950 dark:text-fuchsia-100'
                  )}
                  lang="ta"
                >
                  {right}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    footer,
    hint
  );
};
